import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    FormGroup,
    Label,
    Input,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink
} from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Footer from "components/Footers/Footer";


const baseURL = apiURL1; // Replace with your actual base URL

function TeacherExamList() {
    const token = localStorage.getItem("token");

    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState('');
    const [type, setType] = useState('');
    const [teacherId, setTeacherId] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [examTypes, setExamTypes] = useState([]);

    useEffect(() => {
        // Retrieve user info from localStorage
        const user = localStorage.getItem('userInfo');
        if (user) {
            try {
                const parsedUser = JSON.parse(user);
                setTeacherId(parsedUser._id);
            } catch (error) {
                console.error('Error parsing user info from localStorage:', error);
                toast.error('Error retrieving teacher ID.');
            }
        } else {
            toast.error('User info not found in localStorage.');
        }
    }, []);

    useEffect(() => {
        fetchExamTypes();
    }, []);

    useEffect(() => {
        if (teacherId) {
            fetchExams();
        }
    }, [teacherId, year, type, page]);

    const fetchExamTypes = async () => {
        try {
            const response = await axios.get(`${baseURL}/teacher/getExamTypes`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                setExamTypes(response.data.data);
            } else {
                setExamTypes([]);
                if (response.data && !response.data.success) {
                    toast.error(response.data.message || 'Failed to fetch exam types');
                }
            }
        } catch (error) {
            console.error('Error fetching exam types:', error);
            toast.error('Error fetching exam types');
            setExamTypes([]);
        }
    };

    const fetchExams = async () => {
        setLoading(true);
        try {
            let url = `${baseURL}/teacher/${teacherId}/exams?page=${page}`;
            if (year) url += `&year=${year}`;
            if (type) url += `&type=${type}`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            if (response.data && response.data.data) {
                setExams(response.data.data);
                setTotalPages(response.data.pagination.totalPages);
            } else {
                setExams([]);
                setTotalPages(0);
            }
        } catch (error) {
            console.error('Error fetching exams:', error);
            toast.error('Error fetching exams');
            setExams([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (date) => {
        setYear(dayjs(date).format('YYYY'));
        setPage(1); // Reset page to 1 when year changes
    };

    const handleTypeChange = (e) => {
        setType(e.target.value);
        setPage(1); // Reset page to 1 when type changes
    };

    const renderPagination = () => {
        const paginationItems = [];
        for (let i = 1; i <= totalPages; i++) {
            paginationItems.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination style={{ justifyContent: 'center' }}>
                <PaginationItem disabled={page === 1}>
                    <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {paginationItems}
                <PaginationItem disabled={page === totalPages}>
                    <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
            </Pagination>
        );
    };

    return (
        <>
            <Header />
            <Container>
                <Row className="mb-3">
                    <Col md={6}>
                        <FormGroup>
                            <Label>Year</Label>
                            <DatePicker
                                selected={year ? dayjs(year, 'YYYY').toDate() : null}
                                onChange={handleYearChange}
                                showYearPicker
                                dateFormat="yyyy"
                                yearItemNumber={9}
                                className="form-control"
                                placeholderText="Select Year"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Type</Label>
                            <Input type="select" value={type} onChange={handleTypeChange}>
                                <option value="">All Types</option>
                                {examTypes.map((examType) => (
                                    <option key={examType?._id || 'N/A'} value={examType?._id || 'N/A'}>
                                        {examType?.name || 'N/A'}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        ) : (
                            <>
                                {exams.length === 0 ? (
                                    <p>No exams found.</p>
                                ) : (
                                    exams.map((exam) => (
                                        <Card key={exam._id} className="mb-3">
                                            <CardHeader>
                                                <h5>{exam.class?.className} - {exam.course?.courseName} ({exam?.type?.name} - {exam?.year})</h5>
                                            </CardHeader>
                                            <CardBody>
                                                <div className="table-responsive">
                                                    <Table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th>Subject</th>
                                                                <th>Date</th>
                                                                <th>Start Time</th>
                                                                <th>End Time</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {exam.examDetails.map((detail) => (
                                                                <tr key={detail._id}>
                                                                    <td>{detail?.subject?.name || 'N/A'}</td>
                                                                    <td>{dayjs(detail.examDate).format('DD-MM-YYYY')}</td>
                                                                    <td>{detail.startTime}</td>
                                                                    <td>{detail.endTime}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ))
                                )}
                            </>
                        )}
                        {!loading && totalPages > 0 && renderPagination()}
                    </Col>
                </Row>
                {!loading && totalPages > 0 && <Footer />}

            </Container>
        </>
    );
}

export default TeacherExamList;